import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Table, TableContainer, TableTop } from 'components/table';
import ActionButton from 'components/buttons/action-button/ActionButton';
import InviteTeamMembersModal from '../InviteMembersModal';
import InviteTeamMembersSuccessModal from '../InviteMembersSuccessModal';
import TeamMember from './team-member/TeamMember';
import { withSubscription } from 'modules/subscription/components/SubscriptionHOC';

import { fetchTeamMembers, removeTeamMember } from '../../reducers/teamMembers';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { SUBSCRIPTION_PRODUCT_CM } from 'utils/subscription';
import { getMembersLimit } from 'utils/quota';
import { getFullName } from 'utils/view';
import { __t, applyTranslations } from 'utils/language';
import { isAdmin, isCcmModerator } from 'utils/account';

applyTranslations(require('./team-members.i18n.json'));

const ActionButtonWithSubscription = withSubscription(ActionButton, {
  action: 'onClick',
  product: SUBSCRIPTION_PRODUCT_CM,
  type: 'multiUser',
  fallback: {
    type: 'modal',
    title: __t('team_members.maximum_members_title'),
    message: __t('team_members.maximum_members_message', {
      email: '<a href="mailto:hello@collabary.com">hello@collabary.com</a>' // should be a string
    })
  }
});

class TeamMembers extends React.PureComponent {
  static propTypes = {
    organizationId: PropTypes.number,
    teamMembersMap: PropTypes.object,
    organizationsMapById: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    currentAccount: PropTypes.object,
    asAdmin: PropTypes.bool,
    forceEditMembers: PropTypes.bool,
    ccmStatus: PropTypes.string,
    roles: PropTypes.array
  };

  constructor(props) {
    super(props);

    this.state = this.getDefaultState();
  }

  componentDidMount() {
    this.fetchMembers(this.props);
  }

  getDefaultState() {
    return {
      isInviteMembersModalOpen: false,
      invitedUser: null
    };
  }

  fetchMembers(nextProps) {
    const { organizationId, dispatch } = nextProps;

    if (organizationId) {
      dispatch(fetchTeamMembers(organizationId));
    }
  }

  handleOpenInviteMembersModal = () => {
    this.setState({
      isInviteMembersModalOpen: true
    });
  };

  handleInviteMembersModalClose = (invitedUser) => {
    this.setState({
      isInviteMembersModalOpen: false,
      invitedUser
    });
  };

  handleInviteMembersSuccessModalClose = () => {
    this.setState(this.getDefaultState());
    this.fetchMembers(this.props);
  };

  handleRemove = (member) => {
    const { organizationId, dispatch, currentAccount } = this.props;

    dispatch(showConfirmModal({
      message: __t('team_members.remove_team_member_confirmation', {
        name: getFullName(member)
      }),
      onConfirm: () =>
        dispatch(removeTeamMember(organizationId, member.id))
          .then(() => {
            if (currentAccount && currentAccount.id === member.id) {
              window.location.href = '/user/settings'; // current user has left an organization
              return;
            }
            this.fetchMembers(this.props);
          })
          .then(() => dispatch(hideConfirmModal())),
      onCancel: () =>
        dispatch(hideConfirmModal())
    }));
  };

  render() {
    const {
      teamMembersMap,
      organizationId,
      organizationsMapById,
      asAdmin,
      currentAccount,
      forceEditMembers,
      ccmStatus,
      roles
    } = this.props;
    const teamMembersList = teamMembersMap[organizationId];
    const canEditMembers =
      isAdmin(currentAccount, roles)
        || isCcmModerator(roles)
        || asAdmin
        || organizationsMapById[organizationId]?.type === 'influencer'
        || forceEditMembers
        || ccmStatus !== 'only';

    if (!teamMembersList) {
      return null;
    }

    const teamMembersFoundKey = teamMembersList.length === 1 ?
      'team_members.one_team_member_found_message' :
      'team_members.team_members_found_message';

    const isMembersQuotaExceed = teamMembersList.length >= getMembersLimit(organizationsMapById[organizationId]);
    const isSingleMember = teamMembersList.length === 1;

    return (
      <TableContainer>
        <TableTop
          message={__t(teamMembersFoundKey, {
            count: teamMembersList.length
          })}
        >
          {
            !isMembersQuotaExceed && canEditMembers &&
              <ActionButtonWithSubscription
                subscriptionCheckEnabled={organizationsMapById[organizationId].type === 'brand'}
                organizationId={organizationId}
                onClick={this.handleOpenInviteMembersModal}
                data={{ qa: 'button-invite-team-member' }}
                key={teamMembersList.length}
              >
                {__t('team_members.invite_team_member_button')}
              </ActionButtonWithSubscription>
          }
        </TableTop>
        <Table>
          {
            teamMembersList.length ?
              teamMembersList.map((member) => (
                <TeamMember
                  key={`member-${member.id}`}
                  member={member}
                  isRemovable={!isSingleMember && canEditMembers}
                  handleRemove={this.handleRemove}
                />
              )) :
              <div className="team-members__empty">
                {__t('team_members.empty_member_list')}
              </div>
          }
        </Table>
        <InviteTeamMembersModal
          organizationId={organizationId}
          open={this.state.isInviteMembersModalOpen}
          onClose={this.handleInviteMembersModalClose}
          asAdmin={asAdmin}
        />
        <InviteTeamMembersSuccessModal
          invitedUser={this.state.invitedUser}
          open={Boolean(this.state.invitedUser)}
          onClose={this.handleInviteMembersSuccessModalClose}
        />
      </TableContainer>
    );
  }
}

function mapStateToProps(state) {
  return {
    ccmStatus: state.organizations?.currentOrganization?.ccmStatus ?? 'disabled',
    teamMembersMap: state.teamMembers.mapByOrganizationId,
    currentAccount: state.account.currentAccount,
    roles: state.account.roles
  };
}

export default connect(mapStateToProps)(TeamMembers);
